import { useTranslation } from "react-i18next";
import { ORDER_ACKNOWLEDGED } from "@/features/hr/contracts/consts";
import type { CombinedContractListItem } from "@/features/hr/contracts/types";
import { getIdFromApiUrl } from "@/features/hr/contracts/utils";
import type { ContractAction } from "@/features/hr/contracts/utils/contractRules";
import type { PromptState } from "./useContractModals";
import { useContractModals } from "./useContractModals";
import { useContractDownloads } from "./useContractDownloads";
import { useContractMutations } from "./useContractMutations";

export type { ContractAction, ActionConfig, ContractActions } from "@/features/hr/contracts/utils/contractRules";
export type { PromptState } from "./useContractModals";
export { getAvailableActions } from "@/features/hr/contracts/utils/contractRules";

export interface UseContractsActionsReturn {
  fetchContractDetail: ReturnType<typeof useContractDownloads>["fetchContractDetail"];
  isLoading: {
    isDownloading: boolean;
    isSubmitting: boolean;
    isPreviewing: boolean;
    isLoadingAny: boolean;
  };
  downloadingContractId: number | null;
  previewingContractId: number | null;
  submittingContractId: number | null;
  deletingContractId: number | null;
  previewingJobApplicationCandidateId: number | null;
  reviewingJobApplicationCandidateId: number | null;
  previewingOrderCandidateId: number | null;
  completingHiringCandidateId: number | null;
  documentSigning: ReturnType<typeof useContractMutations>["documentSigning"];
  downloadModal: { id: number } | null;
  setDownloadModal: (state: { id: number } | null) => void;
  prompt: PromptState | null;
  setPrompt: (state: PromptState | null) => void;
  handleDownload: (id: number, contract?: CombinedContractListItem) => Promise<void>;
  handleSubmitForSigning: (id: number) => void;
  handlePreview: (id: number) => Promise<void>;
  handlePreviewJobApplication: (candidateId: number) => Promise<void>;
  handleReviewJobApplication: (
    candidateId: number,
    action: "approve" | "reject" | "revision",
    approval_resolution?: "approved" | "supported" | "no_objection" | "rejected",
    note?: string,
  ) => Promise<void>;
  handlePreviewOrder: (candidateId: number) => Promise<void>;
  handleCompleteHiring: (candidateId: number) => Promise<void>;
  handleDeleteContract: (id: number) => Promise<void>;
  handleAction: (action: ContractAction, contractId: number, contract?: CombinedContractListItem) => Promise<void>;
}

export function useContractsActions(): UseContractsActionsReturn {
  const { t } = useTranslation("ContractsPage");

  const modals = useContractModals();

  const downloads = useContractDownloads(modals.setPrompt);

  const mutations = useContractMutations(modals.setPrompt);

  const isLoadingAny =
    downloads.isDownloading ||
    mutations.isSubmitting ||
    mutations.documentSigning.isBusy ||
    downloads.isPreviewing ||
    downloads.previewingJobApplicationCandidateId !== null ||
    mutations.reviewingJobApplicationCandidateId !== null ||
    downloads.previewingOrderCandidateId !== null ||
    mutations.completingHiringCandidateId !== null ||
    mutations.deletingContractId !== null;

  async function handleAction(action: ContractAction, contractId: number, contract?: CombinedContractListItem) {
    const candidateId = contract?.candidate_application_id;
    const orderId = getIdFromApiUrl(contract?.order_url);

    if ((action === "preview_order" || action === "complete_hiring") && !orderId) {
      modals.setPrompt({
        title: t("messages.errorTitle"),
        text: t("messages.errorTitle"),
        variant: "error",
      });
      return;
    }

    switch (action) {
      case "download":
        await downloads.handleDownload(contractId, contract);
        break;
      case "submit_for_signing":
        mutations.handleSubmitForSigning(contractId);
        break;
      case "submit_for_mobile_signing":
        mutations.handleSubmitForMobileSigning(contractId);
        break;
      case "preview":
        await downloads.handlePreview(contractId);
        break;
      case "preview_job_application":
        if (candidateId) await downloads.handlePreviewJobApplication(candidateId);
        break;
      case "review_job_application":
        if (candidateId) await mutations.handleReviewJobApplication(candidateId, "approve", "approved");
        break;
      case "review_job_application_revision":
        if (candidateId) await mutations.handleReviewJobApplication(candidateId, "revision");
        break;
      case "review_job_application_reject":
        if (candidateId) await mutations.handleReviewJobApplication(candidateId, "reject");
        break;
      case "preview_order":
        if (orderId) await downloads.handlePreviewOrder(orderId);
        break;
      case "complete_hiring":
        if (!orderId) break;
        if (contract?.candidate_stage !== ORDER_ACKNOWLEDGED) {
          modals.setPrompt({
            title: t("order.prompts.notAcknowledged.title"),
            text: t("order.prompts.notAcknowledged.text"),
            variant: "warning",
          });
          break;
        }
        await mutations.handleCompleteHiring(orderId);
        break;
      case "download_contract_pdf":
        await downloads.handleDownload(contractId, contract);
        break;
      case "download_contract_docx":
        await downloads.handlePreview(contractId);
        break;
      case "delete_contract":
        await mutations.handleDeleteContract(contractId);
        break;
      case "edit_contract":
        break;
    }
  }

  return {
    fetchContractDetail: downloads.fetchContractDetail,
    isLoading: {
      isDownloading: downloads.isDownloading,
      isSubmitting: mutations.isSubmitting,
      isPreviewing: downloads.isPreviewing,
      isLoadingAny,
    },
    downloadingContractId: downloads.downloadingContractId,
    previewingContractId: downloads.previewingContractId,
    submittingContractId: mutations.submittingContractId,
    deletingContractId: mutations.deletingContractId,
    previewingJobApplicationCandidateId: downloads.previewingJobApplicationCandidateId,
    reviewingJobApplicationCandidateId: mutations.reviewingJobApplicationCandidateId,
    previewingOrderCandidateId: downloads.previewingOrderCandidateId,
    completingHiringCandidateId: mutations.completingHiringCandidateId,
    documentSigning: mutations.documentSigning,
    downloadModal: modals.downloadModal,
    setDownloadModal: modals.setDownloadModal,
    prompt: modals.prompt,
    setPrompt: modals.setPrompt,
    handleDownload: downloads.handleDownload,
    handleSubmitForSigning: mutations.handleSubmitForSigning,
    handlePreview: downloads.handlePreview,
    handlePreviewJobApplication: downloads.handlePreviewJobApplication,
    handleReviewJobApplication: mutations.handleReviewJobApplication,
    handlePreviewOrder: downloads.handlePreviewOrder,
    handleCompleteHiring: mutations.handleCompleteHiring,
    handleDeleteContract: mutations.handleDeleteContract,
    handleAction,
  };
}

